/**
 * MO-01 rewarded-ad caps per slot per local day, mirroring economy.ads.rewarded,
 * and the MO-01.2 "no dead end" fallback. The API enforces the same caps; this is
 * the client's view so buttons never promise an ad the server will refuse.
 */
import type { KV } from '../storage.js';
import { readJson, writeJson } from '../storage.js';
import { localDayKey } from '../time.js';
import type { AdSlot } from './types.js';

/** Same shape as economy.ads.rewarded. */
export type RewardedCapTable = Partial<Record<AdSlot, { cap: number }>>;

interface CapsState {
  day: string;
  used: Partial<Record<AdSlot, number>>;
}

export class RewardedCaps {
  constructor(
    private readonly caps: RewardedCapTable,
    private readonly kv: KV,
    private readonly dayOf: (ms: number) => string = localDayKey,
    private readonly key = 'ads.rewarded',
  ) {}

  cap(slot: AdSlot): number {
    return this.caps[slot]?.cap ?? 0;
  }

  used(slot: AdSlot, now: number): number {
    return this.today(now).used[slot] ?? 0;
  }

  remaining(slot: AdSlot, now: number): number {
    return Math.max(0, this.cap(slot) - this.used(slot, now));
  }

  canWatch(slot: AdSlot, now: number): boolean {
    return this.remaining(slot, now) > 0;
  }

  /** Count a completed view; returns what is left today. */
  record(slot: AdSlot, now: number): number {
    const s = this.today(now);
    s.used[slot] = (s.used[slot] ?? 0) + 1;
    writeJson(this.kv, this.key, s);
    return this.remaining(slot, now);
  }

  /** Adopt the server's count (AdStartResponse.remainingToday); the server is the authority. */
  syncRemaining(slot: AdSlot, remainingToday: number, now: number): void {
    const s = this.today(now);
    s.used[slot] = Math.max(0, this.cap(slot) - remainingToday);
    writeJson(this.kv, this.key, s);
  }

  private today(now: number): CapsState {
    const day = this.dayOf(now);
    const s = readJson<CapsState>(this.kv, this.key);
    return s && s.day === day ? s : { day, used: {} };
  }
}

export interface NoAdFallback {
  fallback: 'tryLater' | 'gems';
  /** gem price of the alternative (0 for tryLater) */
  gems: number;
}

/**
 * MO-01.2: with no ad, offer the gem alternative when the player can pay it or
 * can reach the store to buy gems; otherwise "Try again soon". Never a dead end.
 */
export function noAdFallback(gemPrice: number | null, gemBalance: number, storeOpen: boolean): NoAdFallback {
  return gemPrice !== null && (gemBalance >= gemPrice || storeOpen) ? { fallback: 'gems', gems: gemPrice } : { fallback: 'tryLater', gems: 0 };
}

export interface RewardedOfferInput {
  adReady: boolean;
  /** RewardedCaps.remaining */
  remaining: number;
  /** e.g. economy.rescue.gems; null when the slot has no gem alternative */
  gemPrice: number | null;
  gemBalance: number;
  /** store reachable: remote flag on, not COPPA-blocked */
  storeOpen: boolean;
}

export type RewardedOffer = { kind: 'ad' } | ({ kind: 'fallback' } & NoAdFallback);

/** What a rewarded button should offer right now. */
export function rewardedOffer(i: RewardedOfferInput): RewardedOffer {
  if (i.adReady && i.remaining > 0) return { kind: 'ad' };
  return { kind: 'fallback', ...noAdFallback(i.gemPrice, i.gemBalance, i.storeOpen) };
}
